import { useCallback, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { useHasOpenBenefitEditors } from "./benefit-edit-scope"
import { BenefitPanelActions } from "./benefit-panel-actions"
import { useBenefitReviewActive } from "@/features/benefit-workspace/benefitEditorContext"
import "./mounted-benefit-tools.css"

/** Move one stable portal between the stash and the sheet without resetting standalone editors.
 * Workspace drafts also survive a complete unmount via Redux. On remount an already-open
 * container must mount before its nested dialogs, or Radix can hide/trap both modal layers.
 */
export function MountedBenefitTools({ open, onClose, onReturnFocus, title, description, scope, children }: {
  open: boolean; onClose: () => void; onReturnFocus: () => void
  title: string; description: string; scope: "program" | "household" | "demographics"; children: ReactNode
}) {
  const [container] = useState(() => document.createElement("div"))
  const [actionsContainer] = useState(() => document.createElement("div"))
  const [hostReady, setHostReady] = useState(false)
  const footerActions = scope === "household"
  const actionsHost = useCallback((node: HTMLDivElement | null) => { if (node) node.appendChild(actionsContainer) }, [actionsContainer])
  const stash = useRef<HTMLDivElement | null>(null)
  const editing = useHasOpenBenefitEditors()
  const reviewing = useBenefitReviewActive()
  const visible = open && !reviewing
  const hiddenHost = useCallback((node: HTMLDivElement | null) => {
    stash.current = node
    if (node && !container.parentNode) node.appendChild(container)
    if (node && !visible) setHostReady(true)
  }, [container, visible])
  const visibleHost = useCallback((node: HTMLDivElement | null) => {
    // Ref detachment parks the same subtree before Radix removes the sheet content.
    const host = node ?? stash.current
    if (host) host.appendChild(container)
    if (node) setHostReady(true)
  }, [container])
  const close = () => {
    if (editing) { toast.error("Save or cancel the open editor before closing this panel."); return }
    onClose()
  }
  return <>
    <div hidden aria-hidden="true" ref={hiddenHost} {...{ [`data-${scope}-tool-stash`]: "" }} />
    {hostReady && createPortal(<BenefitPanelActions.Provider value={footerActions ? actionsContainer : null}>{children}</BenefitPanelActions.Provider>, container)}
    <Sheet open={visible} onOpenChange={value => { if (!value) close() }}>
      <SheetContent className="benefit-tools-sheet w-full sm:max-w-2xl" data-tool-scope={scope} onCloseAutoFocus={event => { event.preventDefault(); if (!reviewing) onReturnFocus() }}>
        <SheetHeader>{footerActions && <p className="household-eyebrow">Household details</p>}<SheetTitle tabIndex={-1}>{title}</SheetTitle><SheetDescription>{description}</SheetDescription></SheetHeader>
        <div className="benefit-tools-body" role="region" aria-label={scope === "program" ? "Program tools" : scope === "household" ? "Household tools" : "Demographics tools"} tabIndex={0} ref={visibleHost} />
        <SheetFooter>
          {editing && <p role="status" className="text-xs text-muted-foreground">{footerActions ? "Changes apply only when saved." : "Save or cancel the open editor before closing this panel."}</p>}
          {footerActions && <div ref={actionsHost} className="benefit-tools-footer-actions" />}
          {(!footerActions || !editing) && <Button variant="outline" disabled={editing} onClick={close}>Done</Button>}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  </>
}
